import { useHistory } from '@useHistory/useHistoryCompat';
import { useEffect, useRef } from 'react';
import { Transition } from 'history';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

export function useCanDeactivate(
  callback: (tx: Transition) => boolean | Promise<boolean>,
  beforeUnloadCallback?: () => boolean,
  blockSamePathCallback?: (tx: Transition) => boolean
) {
  const history = useHistory();
  const callbackRef = useRef(callback);
  const beforeUnloadCallbackRef = useRef(beforeUnloadCallback);
  const blockSamePathCallbackRef = useRef(blockSamePathCallback);
  const unloadListenerRef = useRef(null);
  const unblockRef = useRef(null);
  const onRouteChangeSub = useRef(Subscription.EMPTY);

  callbackRef.current = callback;
  beforeUnloadCallbackRef.current = beforeUnloadCallback;
  blockSamePathCallbackRef.current = blockSamePathCallback;

  useEffect(() => {
    unloadListenerRef.current = () => {
      if (beforeUnloadCallbackRef.current && beforeUnloadCallbackRef.current()) {
        unblockRef.current();
      }
    };

    window.addEventListener('beforeunload', unloadListenerRef.current);

    const setHistoryBlocker = () => {
      return history.block((tx) => {
        if (
          tx.location.pathname === history.location.pathname &&
          (!blockSamePathCallbackRef.current || !blockSamePathCallbackRef.current(tx))
        ) {
          unblockRef.current();

          onRouteChangeSub.current.unsubscribe();

          onRouteChangeSub.current = history.beforeListenersFired$.pipe(take(1)).subscribe((s) => {
            unblockRef.current = setHistoryBlocker();
          });

          tx.retry();

          return;
        }

        Promise.resolve(callbackRef.current(tx)).then((proceed) => {
          if (proceed) {
            unblockRef.current();
            tx.retry();
          }
        });
      });
    };

    unblockRef.current = setHistoryBlocker();

    return () => {
      unblockRef.current();

      onRouteChangeSub.current.unsubscribe();

      if (unloadListenerRef.current) {
        window.removeEventListener('beforeunload', unloadListenerRef.current);
      }
    };
  }, []);
}
